import { inject, Injectable } from '@angular/core';
import { BaseFlowsStore } from '@kinet-open-network/client/flows/data-access';
import { ProductPricesService } from '@kinet-open-network/client/product-prices/data-access';
import { OrderStatus } from '@kinet-open-network/shared/data-access/model';
import { computeVatPriceRow, sumTotals } from '@kinet-open-network/shared/util';
import { firstValueFrom } from 'rxjs';
import { FlowOrders, FlowOrdersStepData } from './flow-orders.model';
import { FlowOrdersService } from './flow-orders.service';

@Injectable()
export class FlowOrdersStore extends BaseFlowsStore<
  FlowOrders,
  'id',
  FlowOrdersStepData
>(FlowOrdersService) {
  private readonly productPricesService = inject(ProductPricesService);
  override readonly service = inject(FlowOrdersService);

  override isTerminalState(status: string): boolean {
    return status === OrderStatus.finished;
  }

  /**
   * Fetches effective prices for all items from the backend
   * (based on customer + date), patches `priceBase`, then recalculates totals.
   */
  async refreshAllItemPrices(customerId: number, date: Date): Promise<void> {
    const model = this.model();
    if (!model?.items?.length) return;

    const results = await Promise.allSettled(
      model.items
        .filter((item: any) => !!item.stockItemId)
        .map(async (item: any) => {
          const price = await firstValueFrom(
            this.productPricesService.getEffectivePrice(
              item.stockItemId,
              customerId,
              date,
            ),
          );
          return {
            stockItemId: item.stockItemId,
            salePrice: price?.salePrice,
          };
        }),
    );

    // Re-read model after async gap — items may have changed
    const current = this.model();
    if (!current?.items?.length) return;

    const priceMap = new Map<string, number>();
    for (const r of results) {
      if (r.status === 'fulfilled' && r.value.salePrice != null) {
        priceMap.set(r.value.stockItemId, r.value.salePrice);
      }
    }

    if (priceMap.size === 0) return;

    let dirty = false;
    const items = current.items.map((item: any) => {
      const newPriceBase = priceMap.get(item.stockItemId);
      if (newPriceBase != null && newPriceBase !== item.priceBase) {
        dirty = true;
        item.priceBase = newPriceBase;
        return computeVatPriceRow(item, 'base');
      }
      return item;
    });

    if (dirty) {
      const totals = sumTotals(items);
      this.patchModel({ items, ...totals } as Partial<FlowOrders>);
    }
  }
}
